import { z } from 'zod';
import { parseCalendarDate, purchaseTotals } from '@klothiq/domain';

const money = z.coerce
  .number()
  .finite()
  .min(0)
  .max(9999999999.99)
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 0.0001, 'Use at most two decimals');
const date = z.string().refine((v) => !!parseCalendarDate(v), 'Enter a valid date');
export const purchaseWorkspaceItemSchema = z
  .object({
    item_id: z.string().uuid().optional(),
    variant_id: z.string().nullable().optional(),
    item_kind: z.enum(['catalog', 'manual']),
    description: z.string().trim().max(300).nullable().optional(),
    inventory_treatment: z.enum(['tracked', 'non_stock', 'link_required']),
    quantity: z.coerce.number().int().positive('Quantity must be at least 1').max(2147483647),
    unit_cost: money,
    discount: money,
  })
  .superRefine((v, ctx) => {
    if (
      v.item_kind === 'catalog' &&
      (!z.string().uuid().safeParse(v.variant_id).success || v.inventory_treatment !== 'tracked')
    )
      ctx.addIssue({ code: 'custom', path: ['variant_id'], message: 'Select a product variant' });
    if (
      v.item_kind === 'manual' &&
      (!v.description || v.inventory_treatment === 'tracked' || v.variant_id)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['description'],
        message: 'Enter a description and select stock handling',
      });
    if (v.discount > v.unit_cost * v.quantity)
      ctx.addIssue({
        code: 'custom',
        path: ['discount'],
        message: 'Discount cannot exceed the item price',
      });
  });
export const purchaseWorkspaceSchema = z
  .object({
    supplier_id: z.string().uuid('Select a supplier'),
    purchase_date: date,
    expected_date: z
      .string()
      .refine((v) => !v || !!parseCalendarDate(v), 'Enter a valid expected date'),
    supplier_invoice_no: z.string().trim().max(100),
    entry_mode: z.enum(['catalog', 'direct']),
    status: z.enum(['draft', 'ordered', 'partially_received', 'received']),
    other_charges: money,
    notes: z.string().trim().max(1000),
    items: z.array(purchaseWorkspaceItemSchema).min(1, 'Add at least one item'),
  })
  .superRefine((v, ctx) => {
    if (purchaseTotals(v.items, v.other_charges).total > 999999999999.99)
      ctx.addIssue({
        code: 'custom',
        path: ['other_charges'],
        message: 'Purchase total exceeds the supported maximum',
      });
    const seen = new Set<string>();
    v.items.forEach((item, index) => {
      if (item.variant_id && seen.has(item.variant_id))
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'variant_id'],
          message: 'This product is already selected',
        });
      if (item.variant_id) seen.add(item.variant_id);
    });
  });
export type PurchaseWorkspaceValues = z.infer<typeof purchaseWorkspaceSchema>;
